// CraftCorps brand colors
export const COLORS = {
  bg: '#050505',
  sky: '#0a0e08',
  dirt: '#603813',
  dirtShadow: '#20260e',
  grassTop: '#93dd45',
  grassSide: '#52b735',
  grassSideTeal: '#2dc988',
  blockShadow: '#20260e',
  barTop: '#93dd45',
  barBottom: '#2dc988',
  glow: 'rgba(45, 201, 136, 0.35)',
};

/** Aggregate raw FFT bins (0..255) into N bar heights (0..1, log-scaled). */
export function binsToBarHeights(bins: Uint8Array, nBars: number): number[] {
  const heights = new Array<number>(nBars);
  const minBin = 1;                              // skip DC offset
  const maxBin = bins.length - 1;
  const logMin = Math.log(minBin);
  const logMax = Math.log(maxBin);
  for (let i = 0; i < nBars; i++) {
    const t0 = i / nBars;
    const t1 = (i + 1) / nBars;
    const startBin = Math.max(minBin, Math.floor(Math.exp(logMin + (logMax - logMin) * t0)));
    const endBin = Math.max(startBin + 1, Math.floor(Math.exp(logMin + (logMax - logMin) * t1)));
    let max = 0;
    for (let b = startBin; b < endBin && b < bins.length; b++) {
      if (bins[b] > max) max = bins[b];
    }
    heights[i] = max / 255;
  }
  return heights;
}

/** Smooth the displayed progress between 2s /now-playing fetches. */
export function interpProgress(lastKnownSeconds: number, fetchAgeSeconds: number): number {
  const capped = Math.min(5, fetchAgeSeconds);
  return lastKnownSeconds + capped;
}

export interface DrawState {
  bars: number[];          // length N, values 0..1
  paused: boolean;
}

const N_BARS = 24;

// Note particles drift up from bar peaks. Live in module-level state so the
// system persists across frames without the caller threading anything through.
type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;        // ms since spawn
  life: number;       // ms total
  glyph: string;
  size: number;
  drift: number;      // horizontal sway amplitude
  hue: number;        // 0..1 between grass-light and grass-teal
};

const GLYPHS = ['♪', '♫', '♬', '♩'];
const particles: Particle[] = [];
const MAX_PARTICLES = 60;

// Frame-to-frame state for animation continuity.
let lastFrameMs = 0;
let breathPhase = 0;       // smoothed bass energy → block scale
let smoothedBars: number[] = [];     // for trailing peak markers
let peakHolds: number[] = [];        // 0..1 trailing peak height per bar
let peakDecayMs: number[] = [];

/**
 * Render one frame onto the canvas: layered atmosphere → grass block stage
 * → spectrum bars rising from the block's top → note particles drifting up.
 * Progress + transport live in the DOM now and are NOT drawn here.
 */
export function draw(canvas: HTMLCanvasElement, state: DrawState): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const W = canvas.width;
  const H = canvas.height;

  const now = performance.now();
  const dt = lastFrameMs ? Math.min(64, now - lastFrameMs) : 16;
  lastFrameMs = now;

  // ── Atmosphere: vertical gradient + soft floor glow ──
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, '#060906');
  sky.addColorStop(0.6, '#050a06');
  sky.addColorStop(1, '#080d08');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);

  // Horizon halo behind where the block lands.
  const blockCenterY = H * 0.72;
  const halo = ctx.createRadialGradient(W / 2, blockCenterY + 60, 10, W / 2, blockCenterY + 60, W * 0.45);
  halo.addColorStop(0, 'rgba(45, 201, 136, 0.18)');
  halo.addColorStop(0.5, 'rgba(45, 201, 136, 0.06)');
  halo.addColorStop(1, 'transparent');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, W, H);

  // ── Bass-driven block breathing ──
  // Use the lowest few bars as a proxy for bass. Smooth with a low-pass.
  const bassEnergy = (state.bars[0] ?? 0) * 0.5 + (state.bars[1] ?? 0) * 0.35 + (state.bars[2] ?? 0) * 0.15;
  breathPhase += (bassEnergy - breathPhase) * 0.08;
  const blockScale = 1 + breathPhase * 0.04;     // up to +4% on heavy bass

  // ── Block sizing & position ──
  const blockW = Math.min(W * 0.72, 620) * blockScale;
  const blockH = blockW * 0.34;
  const cx = W / 2;
  const cy = blockCenterY;

  // Stage disc beneath the block (the "concert" floor light).
  const stageR = blockW * 0.7;
  const stage = ctx.createRadialGradient(cx, cy + blockH * 0.7, 0, cx, cy + blockH * 0.7, stageR);
  stage.addColorStop(0, 'rgba(45, 201, 136, 0.32)');
  stage.addColorStop(0.4, 'rgba(45, 201, 136, 0.12)');
  stage.addColorStop(1, 'transparent');
  ctx.fillStyle = stage;
  ctx.beginPath();
  ctx.ellipse(cx, cy + blockH * 0.7, stageR, stageR * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();

  drawGrassBlock(ctx, cx, cy, blockW, blockH);

  // ── Bars rise from the block's top face ──
  const barAreaLeft = cx - blockW * 0.46;
  const barAreaRight = cx + blockW * 0.46;
  const barAreaWidth = barAreaRight - barAreaLeft;
  const gap = 3;
  const barWidth = (barAreaWidth - (N_BARS - 1) * gap) / N_BARS;
  const maxBarH = Math.min(H * 0.32, 240);
  const grassTopY = cy - blockH;

  // Initialize / grow rolling-state arrays to N_BARS.
  while (smoothedBars.length < N_BARS) smoothedBars.push(0);
  while (peakHolds.length < N_BARS) peakHolds.push(0);
  while (peakDecayMs.length < N_BARS) peakDecayMs.push(0);

  for (let i = 0; i < N_BARS; i++) {
    // Resample bars if the source array is shorter than N_BARS.
    const srcIdx = (i / N_BARS) * state.bars.length;
    const lo = Math.floor(srcIdx);
    const hi = Math.min(state.bars.length - 1, lo + 1);
    const frac = srcIdx - lo;
    const sampled = (state.bars[lo] ?? 0) * (1 - frac) + (state.bars[hi] ?? 0) * frac;

    // Low-pass for visual smoothness (rise fast, fall slow).
    const target = sampled;
    const prev = smoothedBars[i];
    const speed = target > prev ? 0.55 : 0.18;
    smoothedBars[i] = prev + (target - prev) * speed;
    const v = smoothedBars[i];

    // Trailing peak marker.
    if (v > peakHolds[i]) {
      peakHolds[i] = v;
      peakDecayMs[i] = 0;
    } else {
      peakDecayMs[i] += dt;
      const decayStart = 220;
      if (peakDecayMs[i] > decayStart) {
        peakHolds[i] = Math.max(v, peakHolds[i] - (dt / 700));
      }
    }

    const x = barAreaLeft + i * (barWidth + gap);
    const h = Math.max(3, v * maxBarH);

    // Bar gradient — full saturation at top tapering down.
    const grad = ctx.createLinearGradient(0, grassTopY - h, 0, grassTopY);
    grad.addColorStop(0, COLORS.barTop);
    grad.addColorStop(0.75, COLORS.barBottom);
    grad.addColorStop(1, 'rgba(45, 201, 136, 0.7)');
    ctx.fillStyle = grad;
    drawRoundedRect(ctx, x, grassTopY - h, barWidth, h, 2);

    // Bloom glow on the top edge of energetic bars.
    if (v > 0.5) {
      ctx.fillStyle = `rgba(147, 221, 69, ${0.18 * (v - 0.4)})`;
      ctx.fillRect(x - 1, grassTopY - h - 3, barWidth + 2, 3);
    }

    // Peak marker — small dash above the bar.
    const peakH = Math.max(3, peakHolds[i] * maxBarH);
    if (peakHolds[i] > 0.05) {
      ctx.fillStyle = 'rgba(241, 255, 232, 0.7)';
      ctx.fillRect(x, grassTopY - peakH - 4, barWidth, 2);
    }

    // Particle emission: high-energy bar + low rng + below cap.
    if (!state.paused && v > 0.6 && particles.length < MAX_PARTICLES && Math.random() < 0.07) {
      particles.push({
        x: x + barWidth / 2,
        y: grassTopY - h,
        vx: (Math.random() - 0.5) * 18,         // px/sec
        vy: -(30 + Math.random() * 50),          // px/sec, upward
        age: 0,
        life: 1800 + Math.random() * 1400,
        glyph: GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
        size: 12 + Math.random() * 8,
        drift: 12 + Math.random() * 16,
        hue: Math.random(),
      });
    }
  }

  // ── Particles update + draw ──
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.age += dt;
    if (p.age >= p.life) {
      particles.splice(i, 1);
      continue;
    }
    const t = p.age / 1000;
    const tFrac = p.age / p.life;
    const swayX = Math.sin(t * 1.6 + p.x) * p.drift;
    const px = p.x + p.vx * t + swayX;
    const py = p.y + p.vy * t - 8 * t * t;       // mild upward arc
    // Fade in fast, out slow.
    const alpha = tFrac < 0.12 ? tFrac / 0.12 : 1 - (tFrac - 0.12) / 0.88;
    const color = p.hue < 0.5 ? COLORS.barTop : COLORS.barBottom;
    ctx.globalAlpha = Math.max(0, alpha) * 0.85;
    ctx.font = `${p.size}px 'Pixelify Sans', system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.fillText(p.glyph, px, py);
    ctx.globalAlpha = 1;
  }
}

/** Public helper: clear all particles. Useful on track-change so the new
 *  track doesn't inherit a cloud of stale notes from the previous one. */
export function resetVisualizer(): void {
  particles.length = 0;
  smoothedBars.fill(0);
  peakHolds.fill(0);
  peakDecayMs.fill(0);
  breathPhase = 0;
}

function drawGrassBlock(ctx: CanvasRenderingContext2D, cx: number, cy: number, w: number, h: number): void {
  const halfW = w / 2;
  const halfH = h / 2;

  // Top face — slight gradient for depth.
  const topGrad = ctx.createLinearGradient(cx - halfW, cy - h, cx + halfW, cy);
  topGrad.addColorStop(0, '#a4e656');
  topGrad.addColorStop(1, '#7fcb38');
  ctx.fillStyle = topGrad;
  ctx.beginPath();
  ctx.moveTo(cx, cy - h);
  ctx.lineTo(cx + halfW, cy - halfH);
  ctx.lineTo(cx, cy);
  ctx.lineTo(cx - halfW, cy - halfH);
  ctx.closePath();
  ctx.fill();

  // Left face (grass turning to dirt)
  ctx.fillStyle = COLORS.grassSide;
  ctx.beginPath();
  ctx.moveTo(cx - halfW, cy - halfH);
  ctx.lineTo(cx, cy);
  ctx.lineTo(cx, cy + h);
  ctx.lineTo(cx - halfW, cy + halfH);
  ctx.closePath();
  ctx.fill();
  // Dirt portion of left face
  ctx.fillStyle = COLORS.dirt;
  ctx.beginPath();
  ctx.moveTo(cx - halfW, cy - halfH + h * 0.4);
  ctx.lineTo(cx, cy + h * 0.4);
  ctx.lineTo(cx, cy + h);
  ctx.lineTo(cx - halfW, cy + halfH);
  ctx.closePath();
  ctx.fill();

  // Right face — teal-shifted (matches the launcher icon).
  ctx.fillStyle = COLORS.grassSideTeal;
  ctx.beginPath();
  ctx.moveTo(cx + halfW, cy - halfH);
  ctx.lineTo(cx, cy);
  ctx.lineTo(cx, cy + h);
  ctx.lineTo(cx + halfW, cy + halfH);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = COLORS.blockShadow;
  ctx.beginPath();
  ctx.moveTo(cx + halfW, cy - halfH + h * 0.4);
  ctx.lineTo(cx, cy + h * 0.4);
  ctx.lineTo(cx, cy + h);
  ctx.lineTo(cx + halfW, cy + halfH);
  ctx.closePath();
  ctx.fill();

  // Top-edge highlight (catches the eye, suggests light from above).
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.16)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - halfW, cy - halfH);
  ctx.lineTo(cx, cy - h);
  ctx.lineTo(cx + halfW, cy - halfH);
  ctx.stroke();
}

function drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  if (h <= 0 || w <= 0) return;
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  ctx.fill();
}
